/**
 * Slice VW-01 · useVideoWalkSession
 * =================================
 *
 * Hook that owns the Video Walk session lifecycle for the contractor:
 *
 *   - creates a session when the user chooses "Record a Video Walk"
 *   - captures video/mic (or audio-only fallback) via MediaRecorder
 *   - auto-rolls to a new SEGMENT before hitting the per-segment size
 *     ceiling, so the contractor experiences one continuous walk while
 *     the wire actually carries a resilient sequence of small clips
 *   - uploads each segment via POST /video-walks/{sid}/segments as it is
 *     finalized (parallel to the still-running recording)
 *   - exposes elapsed time, segment count, upload progress, camera
 *     availability, and error state so the UI can render a single HUD
 *
 * Contract:
 *   Segments are an IMPLEMENTATION DETAIL. The contractor's Pause / Resume /
 *   Finish / Cancel controls all operate on the SESSION. Segment rollover
 *   is silent.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { isVisionLiveConversationEnabled } from "@/lib/leviVisionLiveFlag";
import { subscribeSpeakingChange } from "@/lib/leviSpeech";

// Per-segment ceiling. Whisper's 25 MB hard cap, minus 2 MB headroom so we
// finalize the current segment before it clips.
const SEGMENT_TARGET_BYTES = 23 * 1024 * 1024;
// How often the recorder flushes chunks. 1 s is a good balance between
// realtime UI feedback and object-size overhead.
const CHUNK_MS = 1000;

const pickMime = (kind) => {
  if (typeof MediaRecorder === "undefined") return "";
  const candidates =
    kind === "video"
      ? ["video/mp4", "video/webm;codecs=vp8,opus", "video/webm"]
      : ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"];
  for (const m of candidates) {
    try {
      if (MediaRecorder.isTypeSupported(m)) return m;
    } catch {
      /* noop */
    }
  }
  return "";
};

export function useVideoWalkSession({ projectId, leviEstimateSessionId, onSessionReady }) {
  const [sessionId, setSessionId] = useState(null);
  const [session, setSession] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | requesting | recording | paused | finalizing | error
  const [elapsedMs, setElapsedMs] = useState(0);
  const [cameraAvailable, setCameraAvailable] = useState(true);
  const [kind, setKind] = useState("video"); // "video" | "audio_fallback"
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);

  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const chunkBytesRef = useRef(0);
  const tickerRef = useRef(null);
  const previewRef = useRef(null); // <video> DOM node for live preview

  // --- Cleanup helpers ---------------------------------------------------

  const stopTicker = () => {
    if (tickerRef.current) {
      clearInterval(tickerRef.current);
      tickerRef.current = null;
    }
  };
  const stopStream = () => {
    try {
      streamRef.current?.getTracks?.().forEach((t) => t.stop());
    } catch {
      /* noop */
    }
    streamRef.current = null;
    // Vision Conversational Deployment · C1 · release the shared-
    // stream reference the Levi drawer may have adopted. The tracks
    // themselves have just been stopped; the drawer's next
    // startListening will notice via _hasHealthyStream() (readyState
    // !== "live") and open its own microphone as usual.
    try {
      if (typeof window !== "undefined") {
        window.__leviVisionMediaStream = null;
      }
    } catch { /* noop */ }
  };

  useEffect(() => () => {
    stopTicker();
    stopStream();
    try {
      recorderRef.current?.stop?.();
    } catch {
      /* noop */
    }
  }, []);

  const bindPreview = useCallback((node) => {
    previewRef.current = node;
    if (node && streamRef.current) {
      try {
        node.srcObject = streamRef.current;
      } catch {
        /* noop */
      }
    }
    // Vision Conversational Deployment · C2 · publish the live
    // preview <video> element for the current-frame snapshot adapter.
    // Consumers (useLeviCommand submit path) query this handle to draw
    // a still frame to a canvas without touching MediaRecorder or the
    // MediaStream. Set on mount, cleared on unmount.
    try {
      if (typeof window !== "undefined") {
        window.__leviVisionPreviewEl = node || null;
      }
    } catch { /* noop */ }
  }, []);

  // --- Session bootstrap -------------------------------------------------

  const ensureSession = useCallback(async () => {
    if (sessionId) return sessionId;
    // V-P1 · Custom Job with Levi Vision (Option B): when a
    // `leviEstimateSessionId` is provided, create a session-scoped
    // Video Walk (project_id=None at capture time). All subsequent
    // segment / finalize operations are keyed by the walk's own sid,
    // so the entire downstream Video Walk pipeline is reused
    // unchanged. Legacy project-scoped callers keep passing
    // `projectId` and continue to hit /projects/{pid}/video-walks
    // exactly as before.
    const createUrl = leviEstimateSessionId
      ? `/levi-estimate-sessions/${leviEstimateSessionId}/video-walks`
      : `/projects/${projectId}/video-walks`;
    const r = await api.post(createUrl, {});
    const s = r.data;
    setSessionId(s.id);
    setSession(s);
    if (onSessionReady) onSessionReady(s);
    return s.id;
  }, [projectId, leviEstimateSessionId, sessionId, onSessionReady]);

  // --- Segment upload ---------------------------------------------------

  const uploadSegment = useCallback(
    async (blob, mimeType, durationMs, sectionLabel) => {
      if (!sessionId) return;
      setUploading(true);
      try {
        const ext =
          mimeType.includes("mp4") ? "mp4"
          : mimeType.includes("quicktime") ? "mov"
          : mimeType.includes("webm") ? "webm"
          : mimeType.includes("ogg") ? "ogg"
          : mimeType.includes("wav") ? "wav"
          : "bin";
        const fd = new FormData();
        const file = new File(
          [blob],
          `video-walk-${Date.now()}.${ext}`,
          { type: mimeType }
        );
        fd.append("file", file);
        const params = new URLSearchParams({
          source: "recorded",
          duration_seconds: String((durationMs || 0) / 1000),
        });
        if (sectionLabel) params.set("section_label", sectionLabel);
        const r = await api.post(
          `/video-walks/${sessionId}/segments?${params.toString()}`,
          fd,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        setSession(r.data);
      } catch (e) {
        setError(e?.response?.data?.detail || e?.message || "Upload failed");
      } finally {
        setUploading(false);
      }
    },
    [sessionId]
  );

  // --- Segment recorder helpers ----------------------------------------

  const finalizeCurrentSegment = useCallback(async ({ sectionLabel } = {}) => {
    const mr = recorderRef.current;
    if (!mr) return;
    // Flush any pending chunk, then stop. onstop assembles the blob and
    // uploads it as a segment.
    return new Promise((resolve) => {
      const mime = mr.mimeType || (kind === "video" ? "video/webm" : "audio/webm");
      const chunks = chunksRef.current;
      const started = mr._startedAt || Date.now();
      const handleStop = async () => {
        chunksRef.current = [];
        chunkBytesRef.current = 0;
        recorderRef.current = null;
        if (chunks.length > 0) {
          const blob = new Blob(chunks, { type: mime });
          await uploadSegment(blob, mime, Date.now() - started, sectionLabel);
        }
        resolve();
      };
      mr.addEventListener("stop", handleStop, { once: true });
      try {
        mr.stop();
      } catch {
        handleStop();
      }
    });
  }, [kind, uploadSegment]);

  const startNewRecorder = useCallback(async () => {
    const stream = streamRef.current;
    if (!stream) throw new Error("Recorder stream not available");
    const mime = pickMime(kind === "audio_fallback" ? "audio" : "video");
    const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
    mr._startedAt = Date.now();
    chunksRef.current = [];
    chunkBytesRef.current = 0;
    mr.ondataavailable = async (ev) => {
      if (!ev.data || ev.data.size === 0) return;
      chunksRef.current.push(ev.data);
      chunkBytesRef.current += ev.data.size;
      if (chunkBytesRef.current >= SEGMENT_TARGET_BYTES) {
        // Silent segment rollover: finalize the current segment and
        // immediately spin up a new one so recording continues without
        // the contractor being interrupted.
        try {
          await finalizeCurrentSegment();
        } catch {
          /* noop */
        }
        try {
          await startNewRecorder();
        } catch {
          /* noop */
        }
      }
    };
    mr.start(CHUNK_MS);
    recorderRef.current = mr;
  }, [finalizeCurrentSegment, kind]);

  // --- Public controls --------------------------------------------------

  const startWalk = useCallback(async () => {
    setError("");
    setStatus("requesting");
    let stream = null;
    let effectiveKind = "video";
    // Vision Conversational Deployment · C1 audio constraints. Explicit
    // echoCancellation / noiseSuppression / autoGainControl so that,
    // when Voice-during-Vision is later attached to the same audio
    // track, Levi's own TTS is filtered by the platform audio engine
    // rather than bleeding through as contractor narration. Falls back
    // gracefully on browsers that do not honor the object form.
    const _visionAudioConstraints = {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    };
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
        audio: _visionAudioConstraints,
      });
    } catch {
      // Camera fallback → audio-only. Do NOT expose this as a third
      // primary capture mode; it is a recovery path only.
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: _visionAudioConstraints,
        });
        effectiveKind = "audio_fallback";
        setCameraAvailable(false);
      } catch (e2) {
        setError(
          "Camera and microphone permission denied. Please allow access and try again, or choose Enter Details Manually."
        );
        setStatus("error");
        return;
      }
    }
    setKind(effectiveKind);
    streamRef.current = stream;
    // Vision Conversational Deployment · C1 · publish the Vision-
    // owned MediaStream so the Levi drawer's startListening can adopt
    // it (iPhone-safe: one MediaStream fanning out to MediaRecorder
    // plus WebAudio + a second MediaRecorder consumer, instead of a
    // second concurrent getUserMedia which iOS Safari does not honor
    // reliably). Cleared by stopStream on Finish/Cancel/error paths.
    try {
      if (typeof window !== "undefined") {
        window.__leviVisionMediaStream = stream;
      }
    } catch { /* noop */ }
    if (previewRef.current) {
      try {
        previewRef.current.srcObject = stream;
      } catch {
        /* noop */
      }
    }
    try {
      await ensureSession();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Could not start a Video Walk.");
      stopStream();
      setStatus("error");
      return;
    }
    try {
      await startNewRecorder();
    } catch (e) {
      setError(e?.message || "Recorder unavailable in this browser.");
      stopStream();
      setStatus("error");
      return;
    }
    setStatus("recording");
    // Vision Conversational Deployment · Checkpoint 1 · D-4 mic
    // arbitration (PO 2026-08-05). Broadcast a window event so the
    // Levi drawer can pause its own microphone loop while the Video
    // Walk owns the mic. Levi remains one continuous assistant; only
    // the drawer's auto-listen loop is released. No new session is
    // created. Listeners are added in ``useLeviCommand``.
    // Checkpoint 2 (2026-08-06) — the event now also carries the
    // ``session_id`` so Levi's server-side ``vision_objective`` can
    // record the video-walk session id and later emit
    // ``vision_finalize_request`` back to this recorder.
    try {
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("levi:vision-walk-started", {
          detail: { session_id: sessionId, kind: effectiveKind },
        }));
      }
    } catch { /* noop */ }
    const t0 = Date.now();
    setElapsedMs(0);
    tickerRef.current = setInterval(() => setElapsedMs(Date.now() - t0), 500);
  }, [ensureSession, startNewRecorder]);

  const pauseWalk = useCallback(() => {
    const mr = recorderRef.current;
    if (mr && mr.state === "recording") {
      try {
        mr.pause();
        setStatus("paused");
      } catch {
        /* noop */
      }
    }
  }, []);

  const resumeWalk = useCallback(() => {
    const mr = recorderRef.current;
    if (mr && mr.state === "paused") {
      try {
        mr.resume();
        setStatus("recording");
      } catch {
        /* noop */
      }
    }
  }, []);

  const finishWalk = useCallback(async () => {
    setStatus("finalizing");
    stopTicker();
    await finalizeCurrentSegment();
    stopStream();
    let finalized = null;
    let finalizeError = null;
    try {
      const r = await api.post(`/video-walks/${sessionId}/finalize`);
      finalized = r.data;
      setSession(r.data);
    } catch (e) {
      finalizeError = e?.response?.data?.detail || e?.message || "Could not finalize.";
      setError(finalizeError);
    }
    setStatus("idle");
    // Stitch 1 · button-finalize continuity event (PO 2026-08-08).
    // Emit `levi:vision-walk-stopped` on the SAME window channel the
    // server-driven finalize path already uses. useLeviCommand.js and
    // any Levi drawer listener treat both paths identically. Payload
    // carries walk_session_id, levi_estimate_session_id, and a
    // `finalized` flag so the drawer can compose a post-walk turn
    // (Stitch 3). Duplicate emission is prevented by useLeviCommand's
    // ref-based de-duplication.
    try {
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("levi:vision-walk-stopped", {
          detail: {
            reason: finalized ? "finalized" : (finalizeError ? "finalize_failed" : "unknown"),
            walk_session_id: sessionId,
            levi_estimate_session_id: leviEstimateSessionId || null,
            finalized: !!finalized,
            transcription_status:
              (finalized && finalized.transcription_status) || null,
          },
        }));
      }
    } catch { /* noop */ }
    return finalized;
  }, [finalizeCurrentSegment, sessionId, leviEstimateSessionId]);

  // Stitch 4 · Multi-segment session control (PO 2026-08-08).
  // "Add Another Segment" preserves the session and uploads the
  // current segment WITHOUT invoking the aggregate finalize pipeline.
  // The recorder returns to idle with the SAME sessionId retained, so
  // the next `startWalk()` reuses `ensureSession` (early-return) and
  // pushes segment[N+1] onto the same walk. Preserves ordering and
  // tenant/session ownership; does NOT trigger Levi wrap-up.
  const saveSegmentAndContinue = useCallback(async () => {
    setStatus("finalizing"); // reuses the "Levi is organizing…" spinner
    stopTicker();
    await finalizeCurrentSegment();
    stopStream();
    setStatus("idle");
    setElapsedMs(0);
  }, [finalizeCurrentSegment]);

  const cancelWalk = useCallback(async () => {
    stopTicker();
    try {
      recorderRef.current?.stop?.();
    } catch {
      /* noop */
    }
    recorderRef.current = null;
    chunksRef.current = [];
    chunkBytesRef.current = 0;
    stopStream();
    if (sessionId) {
      try {
        await api.delete(`/video-walks/${sessionId}`);
      } catch {
        /* noop */
      }
    }
    setSessionId(null);
    setSession(null);
    setElapsedMs(0);
    setStatus("idle");
  }, [sessionId]);

  // Also upload an existing narrated video the contractor selects.
  const uploadExistingFile = useCallback(async (file) => {
    if (!file) return;
    await ensureSession();
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const params = new URLSearchParams({
        source: "uploaded",
        duration_seconds: "0",
      });
      const r = await api.post(
        `/video-walks/${sessionId}/segments?${params.toString()}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setSession(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  }, [ensureSession, sessionId]);

  // Vision Conversational Deployment · Checkpoint 2 (2026-08-06).
  // Listen for Levi-driven lifecycle envelopes and dispatch the
  // matching recorder control. All events are no-ops if the
  // recorder is idle. On finalize/cancel we also emit
  // ``levi:vision-walk-stopped`` so ``useLeviCommand`` can clear
  // the bound session id and free the Levi drawer for the next
  // command.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onFinalize = async () => {
      if (status === "idle") return;
      try { await finishWalk(); } catch { /* noop */ }
      try {
        window.dispatchEvent(new CustomEvent(
          "levi:vision-walk-stopped", { detail: { reason: "finalized" } }));
      } catch { /* noop */ }
    };
    const onCancel = async () => {
      if (status === "idle") return;
      try { await cancelWalk(); } catch { /* noop */ }
      try {
        window.dispatchEvent(new CustomEvent(
          "levi:vision-walk-stopped", { detail: { reason: "cancelled" } }));
      } catch { /* noop */ }
    };
    const onPause = () => {
      try { pauseWalk(); } catch { /* noop */ }
    };
    const onResume = () => {
      try { resumeWalk(); } catch { /* noop */ }
    };
    window.addEventListener("levi:vision-finalize-request", onFinalize);
    window.addEventListener("levi:vision-cancel-request", onCancel);
    window.addEventListener("levi:vision-pause-request", onPause);
    window.addEventListener("levi:vision-resume-request", onResume);
    return () => {
      window.removeEventListener("levi:vision-finalize-request", onFinalize);
      window.removeEventListener("levi:vision-cancel-request", onCancel);
      window.removeEventListener("levi:vision-pause-request", onPause);
      window.removeEventListener("levi:vision-resume-request", onResume);
    };
  }, [status, finishWalk, cancelWalk, pauseWalk, resumeWalk]);

  // P0 RC-5 (PO 2026-08-09) · publish the current Vision recorder
  // state to a small window-scoped diagnostic mirror. The Levi
  // inactivity guard in ``useLeviCommand`` reads this to determine
  // whether Vision is actively working (recording, paused inside
  // an active walkthrough, uploading, or finalizing) so the long
  // idle timer never expires during a live walkthrough. Cleared on
  // unmount so a stale value never bleeds into the next hook
  // instance.
  useEffect(() => {
    try {
      if (typeof window !== "undefined") {
        window.__leviVisionState = {
          ts: Date.now(),
          walk_session_id: sessionId || null,
          status,                    // idle | requesting | recording | paused | finalizing | error
          uploading: !!uploading,
          active: ["recording", "paused", "finalizing", "requesting"].includes(status)
                  || !!uploading,
          segment_count: (session && session.segments && session.segments.length) || 0,
        };
      }
    } catch { /* noop */ }
    return () => {
      try {
        if (typeof window !== "undefined") {
          window.__leviVisionState = null;
        }
      } catch { /* noop */ }
    };
  }, [sessionId, status, uploading, session]);

  // Vision Conversational Deployment · C1 · TTS coordination.
  //
  // While Levi speaks, pause the MediaRecorder so Levi's own TTS is
  // NOT captured into the durable walkthrough transcript. When Levi
  // finishes speaking, resume so contractor narration continues to be
  // captured with no gaps outside the Levi-speaking window. Uses
  // MediaRecorder pause/resume — supported across the three target
  // browsers and non-destructive to the current segment.
  //
  // Safety: an interval sweep resumes the recorder if it is left in
  // "paused" state while Levi is not speaking. This is defence-in-depth
  // in case a subscribeSpeakingChange callback fires while the recorder
  // is transitioning (e.g., a segment rollover). Runs only when the
  // feature flag is ON and the recorder is in an active state.
  useEffect(() => {
    if (!isVisionLiveConversationEnabled()) return undefined;
    if (typeof window === "undefined") return undefined;

    let visionSpeakingActive = false;
    const _pauseIfRecording = () => {
      const mr = recorderRef.current;
      if (mr && mr.state === "recording") {
        try { mr.pause(); } catch { /* noop */ }
      }
    };
    const _resumeIfPaused = () => {
      const mr = recorderRef.current;
      if (mr && mr.state === "paused") {
        try { mr.resume(); } catch { /* noop */ }
      }
    };
    // subscribeSpeakingChange fires true when Levi starts speaking
    // (browser TTS or server audio) and false when he finishes.
    const _unsub = subscribeSpeakingChange((speaking) => {
      // Only intercept while the walk itself is active. When the
      // recorder is idle or finalizing, contractor-controlled pause
      // has already occurred and MediaRecorder should be left alone.
      if (status !== "recording" && status !== "paused") return;
      // While paused by the contractor explicitly, do not resume
      // just because Levi stopped speaking.
      const _ctrPaused = status === "paused";
      visionSpeakingActive = !!speaking;
      if (speaking) {
        _pauseIfRecording();
      } else if (!_ctrPaused) {
        _resumeIfPaused();
      }
    });

    // Defence-in-depth sweep. If Levi is not speaking but the
    // recorder is still paused (e.g., a callback was missed during a
    // segment rollover), resume so contractor narration is not lost.
    const sweep = setInterval(() => {
      if (status !== "recording") return;
      if (visionSpeakingActive) return;
      const mr = recorderRef.current;
      if (mr && mr.state === "paused") {
        try { mr.resume(); } catch { /* noop */ }
      }
    }, 1500);

    return () => {
      try { _unsub && _unsub(); } catch { /* noop */ }
      clearInterval(sweep);
    };
  }, [status]);

  return useMemo(
    () => ({
      sessionId, session, status, kind, elapsedMs, cameraAvailable, error, uploading,
      startWalk, pauseWalk, resumeWalk, finishWalk, cancelWalk, saveSegmentAndContinue,
      uploadExistingFile, bindPreview,
    }),
    [
      sessionId, session, status, kind, elapsedMs, cameraAvailable, error, uploading,
      startWalk, pauseWalk, resumeWalk, finishWalk, cancelWalk, saveSegmentAndContinue,
      uploadExistingFile, bindPreview,
    ]
  );
}
